import React from 'react';
import { SweepstakesDrawer } from './SweepstakesDrawer';
import {
  OptInContent,
  SurveyContent,
  DetailsContent,
  StepContent,
  UnauthenticatedContent,
  ChallengeEndedContent
} from './content';
import { SWEEPSTAKES_CONSTANTS } from '../../../../core/Constants';
import {
  fireDrawerOverlayAnalytics,
  fireOverlayExitAnalytics,
  firePostPageErrorAnalytics,
} from '../../utils/SweepstakesAnalyticsUtils';
import { useSweepstakesDrawer } from '../../../../contexts/SweepstakesDrawerContext';
import { useSweepstakesData } from '../../../../contexts/SweepstakesDataContext';
import { windowExists } from '../../../../common/utils/AccountUtils';
import { SWEEPSTAKES_MOCK_DATA } from '../../../../core/Data/SweepstakesMockData';
import { mapSweepstakesData } from '../../utils/SweepstakesDataMapper';

// Utility functions
const handleCloseDrawer = () => {
  if (windowExists()) {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.close-sweepstakes-drawer');
  }
};

const getDrawerConfig = (drawerType, sweepstakesData, selectedStep, handleStepBack) => {
  // Show logo on all screens except OPT_IN
  const showVisuals = drawerType !== SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN;

  const baseConfig = {
    drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.SWEEPSTAKES_DETAILS,
    sweepstakesTitle: sweepstakesData?.title,
    challengeType: sweepstakesData?.challengeType,
    logoUrl: showVisuals ? sweepstakesData?.logo : null,
    onBack: handleCloseDrawer,
  };

  switch (drawerType) {
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED:
  {
    const unauthenticatedData = mapSweepstakesData(SWEEPSTAKES_MOCK_DATA.unauthenticated);

    return {
      ...baseConfig,
      drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.GIVEAWAY_DETAILS,
      sweepstakesTitle: unauthenticatedData?.title,
      challengeType: unauthenticatedData?.challengeType,
      logoUrl: unauthenticatedData?.logo,
    };
  }
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN:
    return {
      ...baseConfig,
      drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.SWEEPSTAKES_OPT_IN,
      sweepstakesTitle: null,
      heroImage: null,
    };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY:
    return {
      ...baseConfig,
      drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.SWEEPSTAKES_SURVEY,
      onBack: handleStepBack,
    };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS:
    return {
      ...baseConfig,
    };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP:
    return {
      ...baseConfig,
      drawerTitle: selectedStep?.title || baseConfig.drawerTitle,
      onBack: handleStepBack,
    };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.CHALLENGE_ENDED:
    return {
      ...baseConfig,
      drawerTitle: SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.GIVEAWAY_DETAILS,
      sweepstakesTitle: null,
      logoUrl: null,
    };
  default:
    return baseConfig;
  }
};

const getDrawerContent = (
  drawerType,
  optInResult,
  surveyResult,
  detailsResult,
  stepResult,
  unauthenticatedResult,
  challengeEndedResult,
  isPurchaser,
) => {
  if (isPurchaser) {
    return { content: unauthenticatedResult.content, footer: unauthenticatedResult.footer };
  }
  switch (drawerType) {
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED:
    return { content: unauthenticatedResult.content, footer: unauthenticatedResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.OPT_IN:
    return { content: optInResult.content, footer: optInResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY:
    return { content: surveyResult.content, footer: surveyResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.DETAILS:
    return { content: detailsResult.content, footer: detailsResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.STEP:
    return { content: stepResult.content, footer: stepResult.footer };
  case SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.CHALLENGE_ENDED:
    return { content: challengeEndedResult.content, footer: challengeEndedResult.footer };
  default:
    return { content: null, footer: null };
  }
};

export const SweepstakesUnifiedDrawer = () => {
  const {
    drawerState,
    handleStepBack,
  } = useSweepstakesDrawer();

  const {
    sweepstakesData,
    userProfileData,
    forceAcceptSuccess,
    isLoading,
    hasError,
    isChallengeEnded,
    handleOptIn,
    refresh,
    isPurchaser,
  } = useSweepstakesData();

  const { isOpen, drawerType, selectedStep } = drawerState;
  const effectiveDrawerType = isChallengeEnded
    && drawerType !== SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.UNAUTHENTICATED
    ? SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.CHALLENGE_ENDED
    : drawerType;

  // Always call all content hooks in the same order to avoid hook order issues
  const unauthenticatedResult = UnauthenticatedContent({ isPurchaser });
  const challengeEndedResult = ChallengeEndedContent();
  const optInResult = OptInContent({ sweepstakesData, forceAcceptSuccess, handleOptIn, refresh });
  const surveyResult = SurveyContent({
    userProfileData,
    title: sweepstakesData?.title,
    content: sweepstakesData?.content,
    heroImage: sweepstakesData?.heroImage,
    qualtricsProps: {
      isOpen,
      isSurveyDrawerType: effectiveDrawerType === SWEEPSTAKES_CONSTANTS.DRAWER_TYPES.SURVEY
    },
  });
  const detailsResult = DetailsContent({ sweepstakesData });
  const stepResult = StepContent({ step: selectedStep });

  const onCloseDrawer = React.useCallback(() => {
    fireOverlayExitAnalytics(effectiveDrawerType, selectedStep, sweepstakesData?.steps);
    handleCloseDrawer();
  }, [effectiveDrawerType, selectedStep, sweepstakesData]);

  React.useEffect(() => {
    if (!isOpen) return;
    fireDrawerOverlayAnalytics(effectiveDrawerType, selectedStep, sweepstakesData?.steps);
  }, [isOpen, effectiveDrawerType, selectedStep, sweepstakesData?.steps]);

  const drawerErrorFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasError || !isOpen || drawerErrorFiredRef.current) return;
    drawerErrorFiredRef.current = true;
    firePostPageErrorAnalytics(effectiveDrawerType, selectedStep);
  }, [hasError, isOpen, effectiveDrawerType, selectedStep]);

  const config = getDrawerConfig(effectiveDrawerType, sweepstakesData, selectedStep, handleStepBack);
  const onBackDrawer = React.useCallback(() => {
    fireOverlayExitAnalytics(effectiveDrawerType, selectedStep, sweepstakesData?.steps);
    config.onBack();
  }, [effectiveDrawerType, selectedStep, sweepstakesData, config.onBack]);

  const { content, footer } = getDrawerContent(
    effectiveDrawerType,
    optInResult,
    surveyResult,
    detailsResult,
    stepResult,
    unauthenticatedResult,
    challengeEndedResult,
    isPurchaser,
  );

  return (
    <SweepstakesDrawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      onBack={onBackDrawer}
      drawerTitle={config.drawerTitle}
      sweepstakesTitle={config.sweepstakesTitle}
      challengeType={config.challengeType}
      logoUrl={config.logoUrl}
      footerContent={footer}
      isLoading={isLoading}
      hasError={hasError}
      testId="sweepstakes-unified-drawer"
    >
      {content}
    </SweepstakesDrawer>
  );
};

SweepstakesUnifiedDrawer.propTypes = {};

SweepstakesUnifiedDrawer.defaultProps = {};
